// Shared authentication flow, extracted verbatim from the logic in Login.js so
// the landing page (and anywhere else) can perform a *complete* sign-in:
// SHA-256 verify endpoints, email-verification gating, token persistence and
// the /wineries/profile bootstrap that the rest of the app reads
// (userprofile / userId / userEmail consumed by authService + TopMenu).

import { API_BASE, DEFAULT_HEADERS } from "./apiClient";

const JSON_HEADERS = {
  "Content-Type": "application/json",
  ...DEFAULT_HEADERS,
};

// ---- Auth token storage helper (Option B: persistent vs session login) ----
export const tokenStorage = {
  set(token, persistent) {
    const store = persistent ? localStorage : sessionStorage;
    if (token) store.setItem("authToken", token);
    const other = persistent ? sessionStorage : localStorage;
    other.removeItem("authToken");
  },
  get() {
    return (
      localStorage.getItem("authToken") ?? sessionStorage.getItem("authToken")
    );
  },
  clear() {
    localStorage.removeItem("authToken");
    sessionStorage.removeItem("authToken");
  },
};

export const sha256Hex = async (text) => {
  const enc = new TextEncoder();
  const data = enc.encode(text);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
};

const normalizeVerified = (raw) => {
  if (typeof raw === "boolean") return raw;
  if (raw === 0 || raw === 1) return Boolean(raw);
  if (typeof raw === "string") {
    const normalized = raw.trim().toLowerCase();
    if (["true", "verified", "yes", "1"].includes(normalized)) return true;
    if (["false", "unverified", "pending", "0", "no"].includes(normalized))
      return false;
  }
  return null;
};

export const fetchVerificationStatus = async ({ email, userId, token }) => {
  const headers = {
    ...JSON_HEADERS,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  const attempts = [];
  if (email) {
    const qs = encodeURIComponent(email);
    attempts.push({ url: `${API_BASE}/api/auth/email-verify/status?email=${qs}`, options: { method: "GET", headers } });
    attempts.push({ url: `${API_BASE}/api/users/email-verify/status?email=${qs}`, options: { method: "GET", headers } });
    attempts.push({ url: `${API_BASE}/auth/email-verify/status?email=${qs}`, options: { method: "GET", headers } });
    attempts.push({ url: `${API_BASE}/users/email-verify/status?email=${qs}`, options: { method: "GET", headers } });
  }
  if (userId) {
    attempts.push({ url: `${API_BASE}/api/users/email-verify/status`, options: { method: "POST", headers, body: JSON.stringify({ user_id: userId }) } });
    attempts.push({ url: `${API_BASE}/users/email-verify/status`, options: { method: "POST", headers, body: JSON.stringify({ user_id: userId }) } });
  }
  for (const attempt of attempts) {
    try {
      const res = await fetch(attempt.url, attempt.options);
      if (!res.ok) continue;
      const data = await res.json().catch(() => null);
      if (!data) continue;
      const raw =
        data.verified ??
        data.is_verified ??
        data.email_verified ??
        data.user?.is_verified ??
        data.profile?.is_verified ??
        null;
      const normalized = normalizeVerified(raw);
      if (normalized !== null) return normalized;
    } catch (_) {}
  }
  return null;
};

export const resendVerification = async (email) => {
  const emailTarget = (email || "").trim();
  if (!emailTarget) return { ok: false, detail: "" };
  const attempts = [
    `${API_BASE}/api/auth/email-verify/start`,
    `${API_BASE}/api/users/email-verify/start`,
    `${API_BASE}/auth/email-verify/start`,
    `${API_BASE}/users/email-verify/start`,
  ];
  let detail = "";
  for (const url of attempts) {
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: JSON_HEADERS,
        body: JSON.stringify({ email: emailTarget }),
      });
      if (res.ok) return { ok: true, detail: "" };
      detail = (await res.text().catch(() => "")) || detail;
    } catch (e) {
      detail = e.message || detail;
    }
  }
  return { ok: false, detail };
};

export const requestPasswordReset = async (email) => {
  const emailVal = (email || "").trim();
  if (!emailVal) return { ok: false, detail: "" };
  const attempts = [
    `${API_BASE}/users/password-reset`,
    `${API_BASE}/auth/password-reset`,
    `${API_BASE}/users/forgot-password`,
    `${API_BASE}/auth/forgot-password`,
    `${API_BASE}/users/reset/start`,
  ];
  let detail = "";
  for (const url of attempts) {
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: JSON_HEADERS,
        body: JSON.stringify({ email: emailVal }),
      });
      let data = null;
      try {
        data = await res.json();
      } catch {}
      if (res.ok) return { ok: true, detail: "" };
      if (data && (data.error || data.message)) detail = data.error || data.message;
    } catch (e) {
      detail = e.message || detail;
    }
  }
  return { ok: false, detail };
};

// Persist the authenticated session exactly the way Login.js does, then
// bootstrap /wineries/profile into storage so the app sees a real user.
const persistSession = async ({ authToken, authUser, emailVal, rememberMe }) => {
  tokenStorage.set(authToken, rememberMe);

  if (rememberMe) {
    localStorage.setItem("isAuthenticated", "true");
    localStorage.setItem("userEmail", emailVal);
    localStorage.setItem("rememberMe", "true");
    localStorage.setItem("rememberedEmail", emailVal);
    sessionStorage.removeItem("isAuthenticated");
    sessionStorage.removeItem("userEmail");
  } else {
    sessionStorage.setItem("isAuthenticated", "true");
    sessionStorage.setItem("userEmail", emailVal);
    localStorage.setItem("rememberMe", "false");
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("rememberedEmail");
  }

  const storage = rememberMe ? localStorage : sessionStorage;
  const otherStorage = rememberMe ? sessionStorage : localStorage;
  try {
    otherStorage.removeItem("userprofile");
    otherStorage.removeItem("userId");
    otherStorage.removeItem("userEmail");
  } catch (_) {}

  try {
    const headers = {
      ...JSON_HEADERS,
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      ...(authUser?.id ? { "X-User-Id": String(authUser.id) } : {}),
    };
    const emailForQuery = authUser?.email || emailVal || "";
    if (emailForQuery) headers["X-User-Email"] = emailForQuery;

    const profileRes = await fetch(`${API_BASE}/wineries/profile`, {
      method: "GET",
      headers,
    });
    let payload = profileRes.ok ? await profileRes.json() : null;

    if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
      payload = {
        user: {
          id: authUser?.id ?? null,
          email: authUser?.email || emailVal || "",
          username: authUser?.username || "",
        },
        profile: null,
      };
    }

    storage.setItem("userprofile", JSON.stringify(payload || {}));
    const resolvedId =
      payload?.user?.id ?? payload?.profile?.id ?? authUser?.id ?? null;
    if (resolvedId != null) storage.setItem("userId", String(resolvedId));
    const resolvedEmail =
      payload?.user?.email ||
      payload?.profile?.email ||
      authUser?.email ||
      emailVal ||
      "";
    if (resolvedEmail) storage.setItem("userEmail", resolvedEmail);
  } catch (_) {
    const fallbackUser = {
      id: authUser?.id ?? null,
      email: authUser?.email || emailVal || "",
      username: authUser?.username || "",
    };
    try {
      storage.setItem(
        "userprofile",
        JSON.stringify({ user: fallbackUser, profile: null })
      );
      if (fallbackUser.id != null) storage.setItem("userId", String(fallbackUser.id));
      if (fallbackUser.email) storage.setItem("userEmail", fallbackUser.email);
    } catch (_) {}
  }
};

/**
 * Perform a full sign-in. Returns a structured result:
 *  { ok: true }
 *  { ok: false, reason: "validation_email" | "validation_password" }
 *  { ok: false, reason: "unverified", email }
 *  { ok: false, reason: "failed", detail }
 *  { ok: false, reason: "error" }
 * On success, all auth storage is populated; the caller should flip its
 * isLoggedIn state and navigate.
 */
export const performLogin = async ({ email, password, rememberMe = true }) => {
  const emailVal = (email || "").trim();
  const pwdVal = (password || "").trim();

  if (!emailVal) return { ok: false, reason: "validation_email" };
  if (!pwdVal || pwdVal.length < 6)
    return { ok: false, reason: "validation_password" };

  // Pre-flight: block early only on a definitive "unverified"
  try {
    const preStatus = await fetchVerificationStatus({ email: emailVal });
    if (preStatus === false)
      return { ok: false, reason: "unverified", email: emailVal };
  } catch (_) {}

  try {
    const password_hash = await sha256Hex(pwdVal);

    const attempts = [
      { url: `${API_BASE}/users/verify`, body: { email: emailVal, password_hash } },
      { url: `${API_BASE}/users/login`, body: { email: emailVal, password: pwdVal } },
      { url: `${API_BASE}/auth/verify`, body: { email: emailVal, password_hash } },
      { url: `${API_BASE}/auth/login`, body: { email: emailVal, password: pwdVal } },
    ];

    let authed = false;
    let detail = "";
    let authToken = null;
    let authUser = { id: null, email: "", username: "" };
    let lastSuccessPayload = null;

    for (const { url, body } of attempts) {
      try {
        const res = await fetch(url, {
          method: "POST",
          headers: JSON_HEADERS,
          body: JSON.stringify(body),
        });
        let data = null;
        try {
          data = await res.json();
        } catch {}

        if (res.status === 405) {
          detail = `Method not allowed at ${url}`;
          continue;
        }
        if (res.ok && data) {
          if (data.token) authToken = data.token;
          if (data.user) {
            authUser.id = data.user.id ?? null;
            authUser.email = data.user.email ?? "";
            authUser.username = data.user.username ?? "";
          }
          lastSuccessPayload = data;
          authed = true;
          break;
        }
        if (data && (data.error || data.message)) {
          detail = data.error || data.message;
        } else if (!res.ok) {
          detail = `HTTP ${res.status}`;
        }
      } catch (e) {
        detail = e.message || detail;
      }
    }

    if (!authed) return { ok: false, reason: "failed", detail };

    if (!authUser?.email) authUser.email = emailVal;

    // Confirm verification before persisting anything
    const remoteStatus = await fetchVerificationStatus({
      email: authUser?.email || emailVal,
      userId: authUser?.id,
      token: authToken,
    });
    const verifiedFlag =
      typeof remoteStatus === "boolean"
        ? remoteStatus
        : [
            lastSuccessPayload?.user?.is_verified,
            lastSuccessPayload?.is_verified,
            lastSuccessPayload?.profile?.is_verified,
            lastSuccessPayload?.email_verified,
            lastSuccessPayload?.user?.email_verified,
          ]
            .map(normalizeVerified)
            .find((v) => v !== null) ?? null;

    if (verifiedFlag === false || verifiedFlag === null) {
      tokenStorage.clear();
      localStorage.removeItem("isAuthenticated");
      sessionStorage.removeItem("isAuthenticated");
      return { ok: false, reason: "unverified", email: authUser?.email || emailVal };
    }

    await persistSession({ authToken, authUser, emailVal, rememberMe });
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: "error" };
  }
};
